/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Student Assignments tab: collapsible sections of assignment list items.
 * Renders the sections exactly as given; grouping, ordering, tags and the
 * initial expanded state are all decided by the host.
 *
 * @file StudentAssignmentSections.jsx
 * @author Gladson Samuel S
 * @since 3.1.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import AssignmentListItem, {
  defaultConfig as listItemDefaultConfig,
  STATUS_TAG_VARIANTS
} from '../assignments/AssignmentListItem';
import { StudentAssignmentSectionsStylesRoot, classes } from './styles/studentAssignmentSectionsStyles';

export const studentListItemConfig = {
  showDate: true,
  showSections: true,
  showMenu: false,
  showStatusTag: true,
  showCompletionChart: true
};

const StudentAssignmentSections = ({
  sections,
  textInfo,
  listItemConfig,
  className,
  onAssignmentClick,
  onPerformanceReportClick,
  onSectionToggle
}) => {
  const idPrefix = React.useId();
  // Sections follow `defaultExpanded` until the user toggles them.
  const [toggledSections, setToggledSections] = React.useState({});
  const itemConfig = { ...listItemDefaultConfig, ...studentListItemConfig, ...listItemConfig };

  const isExpanded = (section) => toggledSections[section.id] ?? Boolean(section.defaultExpanded);

  const handleToggle = (section) => {
    const nextExpanded = !isExpanded(section);

    setToggledSections((previous) => ({ ...previous, [section.id]: nextExpanded }));
    onSectionToggle?.(section, nextExpanded);
  };

  const renderSectionHeader = (section, listId) => {
    const expanded = isExpanded(section);

    return (
      <h2 className={classes.sectionHeading}>
        <button
          type="button"
          className={classes.sectionToggle}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => handleToggle(section)}
        >
          <span className={classes.sectionTitle}>{section.title}</span>
          <span
            className={[
              classes.sectionChevron,
              !expanded && classes.sectionChevronCollapsed
            ].filter(Boolean).join(' ')}
          >
            <Icon name="ChevronUpIcon" aria-hidden="true" />
          </span>
        </button>
      </h2>
    );
  };

  const renderSectionList = (section, listId) => {
    const expanded = isExpanded(section);

    return (
      <ul id={listId} className={classes.list} hidden={!expanded}>
        {expanded && section.assignments.map((assignment) => (
          <li key={assignment.id}>
            <AssignmentListItem
              assignment={assignment}
              textInfo={textInfo}
              config={itemConfig}
              onSelect={(item) => onAssignmentClick?.(item, section)}
              onPerformanceReportClick={(item) => onPerformanceReportClick?.(item, section)}
            />
          </li>
        ))}
      </ul>
    );
  };

  const renderSection = (section) => {
    const listId = `${idPrefix}-${section.id}`;

    return (
      <section key={section.id} className={classes.section} data-section-id={section.id}>
        {renderSectionHeader(section, listId)}
        {renderSectionList(section, listId)}
      </section>
    );
  };

  return (
    <StudentAssignmentSectionsStylesRoot className={[classes.root, className].filter(Boolean).join(' ')}>
      {sections.map(renderSection)}
    </StudentAssignmentSectionsStylesRoot>
  );
};

StudentAssignmentSections.propTypes = {
  /** Sections in display order. Each assignment uses the AssignmentListItem shape. */
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    defaultExpanded: PropTypes.bool,
    assignments: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      date: PropTypes.string,
      sections: PropTypes.arrayOf(PropTypes.string),
      statusTag: PropTypes.shape({
        label: PropTypes.string.isRequired,
        variant: PropTypes.oneOf(Object.values(STATUS_TAG_VARIANTS))
      }),
      completion: PropTypes.shape({
        completed: PropTypes.number,
        total: PropTypes.number
      }),
      showPerformanceReportButton: PropTypes.bool
    })).isRequired
  })).isRequired,
  /** Passed to every AssignmentListItem: sectionsPrefix, completionLabel, performanceReportLabel. */
  textInfo: PropTypes.shape({
    sectionsPrefix: PropTypes.string,
    completionLabel: PropTypes.string,
    performanceReportLabel: PropTypes.string
  }).isRequired,
  /** Overrides for the list item config. */
  listItemConfig: PropTypes.object,
  className: PropTypes.string,
  /** Called with (assignment, section) when a card is clicked. */
  onAssignmentClick: PropTypes.func,
  /** Called with (assignment, section) when a Performance report button is clicked. */
  onPerformanceReportClick: PropTypes.func,
  /** Called with (section, isExpanded) when a section is expanded or collapsed. */
  onSectionToggle: PropTypes.func
};

StudentAssignmentSections.defaultProps = {
  listItemConfig: {},
  className: undefined,
  onAssignmentClick: null,
  onPerformanceReportClick: null,
  onSectionToggle: null
};

export default StudentAssignmentSections;
